import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { Trash2, Edit2, Plus } from "lucide-react";
import Sidebar from "../Dashboard/Sidebar";
import Topbar from "../Dashboard/Topbar";
import Loader from "../../components/Loader";
import ConfirmModal from "../../components/ConfirmModal";
import {
  PaymentService,
  PaymentPlan,
  PaymentTransaction,
  CreateUpdatePaymentPlanPayload,
} from "../../api/payment";
import { getPatientById } from "../../api/patient";
import AddTransactionModal from "../../components/payment/AddTransactionModal";
import EditTransactionModal from "../../components/payment/EditTransactionModal";

const PaymentPlanDetail: React.FC = () => {
  const navigate = useNavigate();
  const { planId } = useParams();
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState<PaymentPlan | null>(null);
  const [patientName, setPatientName] = useState<string>("");
  const [isAddTransactionModalOpen, setIsAddTransactionModalOpen] =
    useState(false);
  const [isEditTransactionModalOpen, setIsEditTransactionModalOpen] =
    useState(false);
  const [selectedTransaction, setSelectedTransaction] =
    useState<PaymentTransaction | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [transactionToDelete, setTransactionToDelete] =
    useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [isAddChargesModalOpen, setIsAddChargesModalOpen] = useState(false);
  const [additionalChargesAmount, setAdditionalChargesAmount] = useState("");
  const [addChargesLoading, setAddChargesLoading] = useState(false);

  const goBackToPayments = () => {
    navigate("/payments");
  };

  useEffect(() => {
    if (!planId) return;
    fetchPlan();
  }, [planId]);

  const fetchPlan = async () => {
    if (!planId) return;
    setLoading(true);
    try {
      const response = await PaymentService.getPlanById(planId);
      const payload = response?.data?.data || response?.data || {};

      const rawPlan = payload.plan || payload.planData || payload;
      const rawTransactions = payload.transactions || payload.transactionList || [];

      if (!rawPlan) {
        setPlan(null);
        return;
      }

      const normalizedPlan: PaymentPlan = {
        id: rawPlan.planId || rawPlan.id || planId,
        planId: rawPlan.planId || rawPlan.id,
        treatmentId: rawPlan.treatmentId,
        patientId: rawPlan.patientId,
        treatmentPlanId: rawPlan.treatmentPlanId,
        treatmentCost: Number(rawPlan.treatmentCost) || 0,
        discount: Number(rawPlan.discount) || 0,
        additionalCharges: Number(rawPlan.additionalCharges) || 0,
        reasonForExtraCharge: rawPlan.reasonForExtraCharge,
        addComment: rawPlan.addComment || rawPlan.comment || "",
        totalPayable: Number(rawPlan.totalPayable) || undefined,
        totalReceived: Number(rawPlan.totalReceived) || undefined,
        pendingAmount:
          Number(
            rawPlan.pendingAmount !== undefined
              ? rawPlan.pendingAmount
              : rawPlan.remainingAmount
          ) || undefined,
        remainingAmount:
          rawPlan.remainingAmount !== undefined
            ? Number(rawPlan.remainingAmount)
            : undefined,
        status: rawPlan.status,
      };

      const normalizedTransactions: PaymentTransaction[] = Array.isArray(
        rawTransactions
      )
        ? rawTransactions.map((t: any): PaymentTransaction => ({
            id: t.transactionId || t.id || "",
            transactionId: t.transactionId || t.id,
            planId: t.planId || planId,
            amountReceived: Number(t.amountReceived) || 0,
            paymentDate: t.paymentDate || "",
            patientId: t.patientId,
            treatmentId: t.treatmentId,
            paymentNumber: t.paymentNumber,
            addComment: t.addComment || t.comment || "",
          }))
        : [];

      const finalPlan: PaymentPlan = {
        ...normalizedPlan,
        transactions: normalizedTransactions,
      };

      setPlan(finalPlan);

      // Fetch patient name for display using patientId from plan
      const patientId = finalPlan.patientId;
      if (patientId) {
        try {
          const patientRes = await getPatientById(String(patientId));
          const pData = patientRes?.data?.data || patientRes?.data || {};
          const name =
            pData.name ||
            pData.fullName ||
            (pData.firstName && pData.lastName
              ? `${pData.firstName} ${pData.lastName}`
              : "") ||
            "";
          if (name) {
            setPatientName(name);
          } else {
            setPatientName("");
          }
        } catch {
          setPatientName("");
        }
      } else {
        setPatientName("");
      }
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || "Failed to load payment plan"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleAddTransaction = () => {
    setIsAddTransactionModalOpen(true);
  };

  const handleEditTransaction = (transaction: PaymentTransaction) => {
    setSelectedTransaction(transaction);
    setIsEditTransactionModalOpen(true);
  };

  const handleDeleteTransaction = (transactionId: string) => {
    setTransactionToDelete(transactionId);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!transactionToDelete) return;
    setDeleteLoading(true);
    try {
      await PaymentService.deleteTransaction(transactionToDelete);
      toast.success("Transaction deleted successfully!");
      setIsDeleteModalOpen(false);
      setTransactionToDelete(null);
      fetchPlan(); // Refresh plan data
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || "Failed to delete transaction"
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleTransactionSuccess = () => {
    setIsAddTransactionModalOpen(false);
    setIsEditTransactionModalOpen(false);
    setSelectedTransaction(null);
    fetchPlan(); // Refresh plan data
  };

  const handleAddCharges = () => {
    setIsAddChargesModalOpen(true);
    setAdditionalChargesAmount("");
  };

  const handleAddChargesSubmit = async () => {
    if (!plan || !additionalChargesAmount.trim()) {
      toast.error("Please enter a valid amount");
      return;
    }

    const newAmount = parseFloat(additionalChargesAmount);
    if (isNaN(newAmount) || newAmount <= 0) {
      toast.error("Please enter a valid positive number");
      return;
    }

    setAddChargesLoading(true);
    try {
      // Prepare payload with existing plan data + only new additional charges
      const payload = {
        id: plan.planId || plan.id,
        treatmentId: plan.treatmentId || "",
        patientId: plan.patientId || "",
        doctorId: "", // Will be handled by backend
        treatmentPlanId: plan.treatmentPlanId || "",
        addComment: plan.addComment || "",
        treatmentCost: plan.treatmentCost || 0,
        discount: plan.discount || 0,
        additionalCharges: newAmount, // Only new amount, backend will handle adding to existing
        reasonForExtraCharge: plan.reasonForExtraCharge || "",
      };

      await PaymentService.createOrUpdatePlan(payload);
      toast.success("Additional charges added successfully!");
      setIsAddChargesModalOpen(false);
      setAdditionalChargesAmount("");
      fetchPlan(); // Refresh plan data
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || "Failed to add additional charges"
      );
    } finally {
      setAddChargesLoading(false);
    }
  };

  if (!plan && !loading) {
    return (
      <div className="flex h-screen bg-gray-100">
        <Sidebar
          isOpen={isSidebarOpen}
          toggle={() => setIsSidebarOpen(!isSidebarOpen)}
        />
        <div className="flex-1 flex flex-col">
          <Topbar toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />
          <div className="flex-1 flex items-center justify-center">
            <p className="text-gray-500">Payment plan not found</p>
          </div>
        </div>
      </div>
    );
  }

  const transactions: PaymentTransaction[] = plan?.transactions || [];
  const safeTreatmentCost = Number.isFinite(Number(plan?.treatmentCost))
    ? Number(plan?.treatmentCost)
    : 0;
  const safeDiscount = Number.isFinite(Number(plan?.discount))
    ? Number(plan?.discount)
    : 0;
  const safeAdditional = Number.isFinite(Number(plan?.additionalCharges))
    ? Number(plan?.additionalCharges)
    : 0;

  const totalReceived = transactions.reduce(
    (sum: number, t: PaymentTransaction) => sum + (t.amountReceived || 0),
    0
  );
  const totalAmount = safeTreatmentCost - safeDiscount + safeAdditional;
  const pendingAmount = totalAmount - totalReceived;

  return (
    <div className="flex h-screen bg-gray-100">
      <ToastContainer position="top-right" autoClose={3000} />
      {loading && <Loader />}
      <Sidebar
        isOpen={isSidebarOpen}
        toggle={() => setIsSidebarOpen(!isSidebarOpen)}
      />

      <div className="flex-1 flex flex-col overflow-hidden">
        <Topbar toggleSidebar={() => setIsSidebarOpen(!isSidebarOpen)} />

        <div className="flex-1 overflow-y-auto p-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="max-w-6xl mx-auto"
          >
            {plan && (
              <>
                {/* Plan Details Card */}
                <div className="w-full rounded-3xl shadow-2xl bg-white p-6 md:p-8 mb-6">
                  {/* Header inside card, consistent with other forms */}
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl md:text-2xl font-bold text-gray-800">
                      Payment Plan Details
                    </h2>
                      <button
                        type="button"
                        className="px-4 py-1 rounded-xl bg-gray-100 text-gray-800 border border-gray-200 hover:bg-gray-200 transition flex items-center gap-2"
                        onClick={goBackToPayments}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="w-4 h-4"
                          aria-hidden="true"
                        >
                          <path d="M10 19l-7-7 7-7" />
                          <path d="M3 12h18" />
                        </svg>
                        Back
                      </button>
                  </div>

                  <h3 className="text-lg font-semibold text-gray-800 mb-4">
                    Plan Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-600">
                        Patient
                      </label>
                      <p className="text-gray-800 mt-1">
                        {patientName || "N/A"}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-600">
                        Treatment Cost
                      </label>
                      <p className="text-gray-800 mt-1">
                        ₹{safeTreatmentCost.toFixed(2)}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-600">
                        Discount
                      </label>
                      <p className="text-gray-800 mt-1">
                        ₹{safeDiscount.toFixed(2)}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-600">
                        Additional Charges
                      </label>
                      <div className="flex items-center justify-between mt-1">
                        <p className="text-gray-800">
                          ₹{safeAdditional.toFixed(2)}
                        </p>
                        <button
                          onClick={handleAddCharges}
                          className="ml-4 px-3 py-1 text-xs rounded-lg bg-[#016683] text-white hover:opacity-90 transition flex items-center gap-1"
                        >
                          <Plus className="w-3 h-3" />
                          Add more
                        </button>
                      </div>
                    </div>
                    {plan.reasonForExtraCharge && (
                      <div className="md:col-span-2">
                        <label className="text-sm font-medium text-gray-600">
                          Reason for Extra Charge
                        </label>
                        <p className="text-gray-800 mt-1">
                          {plan.reasonForExtraCharge}
                        </p>
                      </div>
                    )}
                    {plan.addComment && (
                      <div className="md:col-span-2">
                        <label className="text-sm font-medium text-gray-600">
                          Comments
                        </label>
                        <p className="text-gray-800 mt-1">{plan.addComment}</p>
                      </div>
                    )}
                  </div>

                  {/* Summary */}
                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <label className="text-sm font-medium text-gray-600">
                          Total Amount
                        </label>
                        <p className="text-xl font-bold text-[#016683] mt-1">
                          ₹{totalAmount.toFixed(2)}
                        </p>
                      </div>
                      <div className="bg-green-50 p-4 rounded-lg">
                        <label className="text-sm font-medium text-gray-600">
                          Total Received
                        </label>
                        <p className="text-xl font-bold text-green-600 mt-1">
                          ₹{totalReceived.toFixed(2)}
                        </p>
                      </div>
                      <div
                        className={`p-4 rounded-lg ${
                          pendingAmount > 0
                            ? "bg-red-50"
                            : pendingAmount < 0
                            ? "bg-yellow-50"
                            : "bg-gray-50"
                        }`}
                      >
                        <label className="text-sm font-medium text-gray-600">
                          Pending Amount
                        </label>
                        <p
                          className={`text-xl font-bold mt-1 ${
                            pendingAmount > 0
                              ? "text-red-600"
                              : pendingAmount < 0
                              ? "text-yellow-600"
                              : "text-gray-600"
                          }`}
                        >
                          ₹{Math.abs(pendingAmount).toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Transactions Section */}
                <div className="bg-white rounded-lg shadow-md p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-semibold text-gray-800">
                      Transactions
                    </h2>
                    <button
                      onClick={handleAddTransaction}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#016683] text-white hover:opacity-90 transition"
                    >
                      <Plus className="w-4 h-4" />
                      Add Transaction
                    </button>
                  </div>

                  {transactions.length === 0 ? (
                    <p className="text-gray-500 text-center py-8">
                      No transactions yet. Add your first transaction.
                    </p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-gray-200">
                            <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">
                              Date
                            </th>
                            <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">
                              Amount Received
                            </th>
                            <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">
                              Comment
                            </th>
                            <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {transactions.map((transaction: PaymentTransaction) => (
                            <tr
                              key={transaction.id}
                              className="border-b border-gray-100 hover:bg-gray-50"
                            >
                              <td className="py-3 px-4 text-sm text-gray-800">
                                {new Date(
                                  transaction.paymentDate
                                ).toLocaleDateString()}
                              </td>
                              <td className="py-3 px-4 text-sm font-medium text-gray-800">
                                ₹{transaction.amountReceived.toFixed(2)}
                              </td>
                              <td className="py-3 px-4 text-sm text-gray-600">
                                {transaction.addComment || "-"}
                              </td>
                              <td className="py-3 px-4">
                                <div className="flex justify-end gap-2">
                                  <button
                                    onClick={() =>
                                      handleEditTransaction(transaction)
                                    }
                                    className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition"
                                    title="Edit Transaction"
                                  >
                                    <Edit2 className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() =>
                                      handleDeleteTransaction(transaction.id)
                                    }
                                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                                    title="Delete Transaction"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </>
            )}
          </motion.div>
        </div>
      </div>

      {/* Add Transaction Modal */}
      {planId && (
        <AddTransactionModal
          isOpen={isAddTransactionModalOpen}
          onClose={() => setIsAddTransactionModalOpen(false)}
          onSuccess={handleTransactionSuccess}
          planId={planId}
        />
      )}

      {/* Edit Transaction Modal */}
      {selectedTransaction && (
        <EditTransactionModal
          isOpen={isEditTransactionModalOpen}
          onClose={() => {
            setIsEditTransactionModalOpen(false);
            setSelectedTransaction(null);
          }}
          onSuccess={handleTransactionSuccess}
          transaction={selectedTransaction}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={isDeleteModalOpen}
        title="Delete Transaction"
        message="Are you sure you want to delete this transaction? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDelete}
        onCancel={() => {
          setIsDeleteModalOpen(false);
          setTransactionToDelete(null);
        }}
        loading={deleteLoading}
        variant="danger"
      />

      {/* Add Additional Charges Modal */}
      {isAddChargesModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="relative z-10 w-full max-w-md bg-white rounded-2xl shadow-2xl p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-800">
                Add Additional Charges
              </h3>
              <button
                type="button"
                onClick={() => setIsAddChargesModalOpen(false)}
                className="text-gray-500 hover:text-gray-700"
                aria-label="Close"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="w-5 h-5"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Amount (₹)
              </label>
              <input
                type="text"
                value={additionalChargesAmount}
                onChange={(e) => {
                  const value = e.target.value;
                  // Allow only numbers and decimal point
                  if (value === "" || /^\d*\.?\d*$/.test(value)) {
                    setAdditionalChargesAmount(value);
                  }
                }}
                placeholder="Enter amount"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#016683] focus:border-transparent"
              />
            </div>

            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setIsAddChargesModalOpen(false)}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition"
                disabled={addChargesLoading}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleAddChargesSubmit}
                disabled={addChargesLoading || !additionalChargesAmount.trim()}
                className="px-4 py-2 rounded-lg bg-[#016683] text-white hover:opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {addChargesLoading && (
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                )}
                Add Charges
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default PaymentPlanDetail;

